import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFujiAuth } from '../../../auth/useFujiAuth';
import BoughtTicketList from '../../../components/BoughtTicketList/BoughtTicketList';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import SmallLoading from '../../../components/SmallLoading/SmallLoading';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';
import { showNotificationDialog } from '../../../utils';
import { CacheManager } from '../../../utils/CacheManager/CacheManager';
import { isUsableTicket } from '../../../utils';
import { localStorageKeys, ORDER_STATUS } from '../../../utils/Constants';
import useR05Api from '../../../api/useR05Api';
import BorderButton from '../../../components/BorderButton/BorderButton';
import './styles.css';
import { ErrorType, OrderTicketList } from 'types';

/**
 * 購入済みチケット一覧画面
 */
const BoughtTicketListScreen = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [smallLoading, setSmallLoading] = useState(false);
    const [tickets, setTickets] = useState<OrderTicketList[]>([]);
    const { isAuthenticated } = useFujiAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { fetchOrderList, fetchHolidays } = useR05Api();

    /**
     * エラーが発生した時のNotification
     * @param {Error} error
     */
    const showErrorNotification = useCallback(
        (error: ErrorType) => {
            if (String(error).includes('offline') || String(error).includes('timeout')) {
                showNotificationDialog('error', '', t('notification.title'), t('notification.network-failed'));
            } else {
                showNotificationDialog('error', '', t('notification.title'), t('notification.failed'));
            }
        },
        [t],
    );

    /**
     * 使用前チケットの取得
     */
    const fetchBoughtTickets = useCallback(async () => {
        setLoading(true);

        const cacheName = 'bought_ticket_orders_cache';
        const cacheUrl = `${location.pathname}/ticketOrders.json`;
        const cacheManager = new CacheManager(cacheName, cacheUrl);

        // オンライン時のfetch
        const fetchOnline = async () => {
            let purchasedOrdersList = (
                await fetchOrderList({
                    status: [
                        ORDER_STATUS.BOUGHT,
                        ORDER_STATUS.USER_REFUND_ENABLED,
                        ORDER_STATUS.STOCK_USER_REFUND_ENABLED,
                    ].join(','),
                })
            )?.filter(isUsableTicket);
            const holidays = await fetchHolidays();
            if (purchasedOrdersList == null) {
                return;
            }
            purchasedOrdersList = purchasedOrdersList?.map((item) => {
                // 販売チケット名のキーを分離
                return { ...item, saleTicketName: item?.ticketName, holidays };
            });
            setTickets(purchasedOrdersList ?? []);
            await cacheManager.saveToCache(purchasedOrdersList ?? []);
        };

        // オフライン or 低速時のfetch
        const fetchOffline = async () => {
            const response = await cacheManager.readFromCache();
            setTickets(response);
        };

        // キャッシュのデータをまず表示、エラー時には何もしない
        try {
            await fetchOffline();
        } catch {}
        setLoading(false);

        // オンライン時は、ポップアップのローディングを出して、チケット一覧を更新する
        if (navigator.onLine) {
            setSmallLoading(true);
            try {
                await fetchOnline();
            } catch (error: any) {
                console.error(error);
                showErrorNotification(error);
            }
            setSmallLoading(false);
        } else {
            showNotificationDialog('error', '', t('notification.title'), t('ticket-display.no-ticket-cache'));
        }
    }, [fetchHolidays, fetchOrderList, location, showErrorNotification, t]);

    useEffect(() => {
        if (isAuthenticated) {
            void fetchBoughtTickets();
        }
    }, [isAuthenticated, fetchBoughtTickets]);

    /**
     * リロードボタンを押した時の挙動
     */
    const onTapRefetch = useCallback(() => {
        if (!smallLoading) {
            setSmallLoading(true);
            void fetchBoughtTickets().then(() => {
                setSmallLoading(false);
            });
        }
    }, [fetchBoughtTickets, smallLoading]);

    /**
     * 使用済みへボタンを押した時
     */
    const handleClickToUsed = useCallback(() => {
        navigate('/bought/used');
    }, [navigate]);

    /**
     * ログインはこちらボタンを押した時
     * ログイン後にこの画面に戻れるようにlocalStorageに保持する
     */
    const handleClickToSignIn = useCallback(() => {
        localStorage.setItem(
            localStorageKeys.LOGIN_FLOW,
            JSON.stringify({
                path: '/bought',
            }),
        );
        navigate('/auth');
    }, [navigate]);

    return (
        <>
            {loading && <CenterLoading />}
            <SmallLoading show={smallLoading} content={t('loading.small-bought-ticket-list') ?? ''} />
            <Header title={t('header.bought-ticket')} refetch={onTapRefetch} />
            {isAuthenticated ? (
                tickets.length > 0 ? (
                    <div className="content-container scrollable">
                        <BoughtTicketList ticketList={tickets} />
                        <BorderButton onClick={handleClickToUsed} text={t('button.to-bought-ticket-list')} />
                    </div>
                ) : (
                    <div className="no-ticket-view">
                        <TextBreaks
                            text={
                                navigator.onLine
                                    ? smallLoading
                                        ? t('loading.small-bought-ticket-list')
                                        : t('ticket-display.no-ticket')
                                    : t('ticket-display.no-ticket-cache')
                            }
                        />
                        <BorderButton onClick={handleClickToUsed} text={t('button.to-bought-ticket-list')} />
                    </div>
                )
            ) : (
                <div className="no-ticket-view">
                    <TextBreaks text={t('notification.authentication-failed')} />
                    <BorderButton onClick={handleClickToSignIn} text={t('button.to-sign-in')} />
                </div>
            )}
        </>
    );
};

export default BoughtTicketListScreen;
